
/* ============================== src/components/TagsList.jsx ============================== */
// src/components/TagsList.jsx
//
// Строка чипсов. Единый рендер для модалки (interactive=false →
// <button>) и карточек (interactive=true → Clickable as="div").
//
// Почему на карточках не <button>: карточка сама часто является
// Clickable as="div" (role=button). Вложенный <button> внутри
// role=button - конфликт семантики и лишний Tab-стоп для скринридера.
// Clickable as="div" даёт role=button, tabIndex и keyboard - то же,
// что <button>, но без вложенного интерактива внутри интерактива.
//
// stopPropagation обязателен: без него клик по чипу открыл бы
// родительскую сущность вместо TagResultsModal.
//
// maxVisible - опциональный лимит видимых чипсов. Если задан и
// tags.length > maxVisible, показываются первые maxVisible, остальные
// сворачиваются в чип «+N». Клик по «+N» вызывает onTagClick с первым
// скрытым тегом - модалка TagResultsModal открывается на нём, и
// пользователь видит всё, что относится к теме. Отдельный проп
// onShowAll не вводим: интерфейс остаётся минимальным (I из SOLID).
//
// Имена скрытых меток доступны через нативный title на чипе «+N».
// Требование промпта - клик по «+N» эквивалентен клику по метке -
// сохранено: title не перехватывает клик, только показывает подсказку
// при наведении. Отдельный тултип-компонент для этого не нужен:
// список уже вычислен локально, нативный title решает задачу без
// нового состояния, новых пропсов и позиционной логики (KISS).
//
// Вся арифметика slice + остаток - здесь, в одном месте. TaskCard
// передаёт только число, не считает.
import { Clickable } from "./Clickable";
import { TAG_CHIP_ARIA_TEMPLATE } from "../utils/tags";

// Список скрытых меток в подсказке. Префикс отделён от разделителя -
// если формат понадобится поменять, меняется одна константа рядом
// с местом использования, а не разбросанная по JSX строка.
const MORE_TAGS_TITLE_PREFIX = "Ещё метки: ";
const MORE_TAGS_TITLE_SEPARATOR = ", ";

export function TagsList({
  tags,
  onTagClick,
  className = "",
  interactive = false,
  maxVisible,
}) {
  if (!Array.isArray(tags) || tags.length === 0) return null;

  const limitApplied =
    typeof maxVisible === "number" &&
    maxVisible >= 0 &&
    tags.length > maxVisible;
  const visibleTags = limitApplied ? tags.slice(0, maxVisible) : tags;
  const hiddenTags = limitApplied ? tags.slice(maxVisible) : [];
  const hiddenCount = hiddenTags.length;
  const firstHiddenTag = hiddenCount > 0 ? hiddenTags[0] : null;
  const moreAriaLabel = `Показать все метки, скрыто ещё ${hiddenCount}`;
  // title показывается браузером при наведении - список имён скрытых
  // меток. Имена не дублируют aria-label: aria-label для скринридера
  // и title для мыши решают разные задачи, объединять их не нужно.
  const moreTitle =
    hiddenCount > 0
      ? MORE_TAGS_TITLE_PREFIX + hiddenTags.join(MORE_TAGS_TITLE_SEPARATOR)
      : undefined;

  const containerClass = `flex flex-wrap items-center gap-1${className ? " " + className : ""}`;

  if (interactive) {
    return (
      <div className={containerClass}>
        {visibleTags.map((tag) => (
          <Clickable
            key={tag}
            as="div"
            className="st-chip cursor-pointer"
            aria-label={TAG_CHIP_ARIA_TEMPLATE(tag)}
            onClick={(e) => {
              e.stopPropagation();
              onTagClick(tag);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {tag}
          </Clickable>
        ))}
        {hiddenCount > 0 && (
          <Clickable
            as="div"
            className="st-chip st-chip--more cursor-pointer"
            aria-label={moreAriaLabel}
            title={moreTitle}
            onClick={(e) => {
              e.stopPropagation();
              onTagClick(firstHiddenTag);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            +{hiddenCount}
          </Clickable>
        )}
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {visibleTags.map((tag) => (
        <button
          key={tag}
          type="button"
          className="st-chip cursor-pointer"
          aria-label={TAG_CHIP_ARIA_TEMPLATE(tag)}
          onClick={() => onTagClick(tag)}
        >
          {tag}
        </button>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="st-chip st-chip--more cursor-pointer"
          aria-label={moreAriaLabel}
          title={moreTitle}
          onClick={() => onTagClick(firstHiddenTag)}
        >
          +{hiddenCount}
        </button>
      )}
    </div>
  );
}
